'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { ForgotPassword, ResetPassword, VerifyEmailOTP } from '@/actions';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { logger } from '@/lib/logger';
import { forgotPasswordSchema, otpVerificationSchema, resetPasswordSchema } from '@/zod-schema';

/**
 * @file forgot-password-form.jsx
 * @description Create forgot password form
 * @version 0.0.1
 * Created at: 23/09/2025
 * Updated at: 23/09/2025
 */
export const ForgotPasswordForm = () => {
  //-------------- State & Variables --------------//
  const [formType, setFormType] = useState('forgotPassword');
  const router = useRouter();
  const sendEmailForm = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const verifyEmailOtpForm = useForm({
    resolver: zodResolver(otpVerificationSchema),
    defaultValues: {
      otp: '',
    },
  });

  const resetPasswordForm = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const {
    formState: { isSubmitting: isSendEmailSubmitting },
    getValues: getSendEmailFormValues,
  } = sendEmailForm;

  const {
    formState: { isSubmitting: isVerifyEmailOtpSubmitting },
    setError,
    getValues: getResetPasswordFormValues,
  } = verifyEmailOtpForm;

  const {
    formState: { isSubmitting: isResetPasswordSubmitting },
  } = resetPasswordForm;

  const email = getSendEmailFormValues('email');
  const otp = getResetPasswordFormValues('otp');

  //-------------- Use Effects --------------//

  //-------------- Other Methods --------------//

  /**
   * Handle email submission
   */
  const onEmailSend = async (values) => {
    const toastId = toast;
    try {
      toast.loading('Email sending...', { id: toastId });

      const res = await ForgotPassword(values);

      if (!res.success) toast.error('Something went wrong. Please try again.', { id: toastId });
      toast.success("We've sent you an email to reset your password.", { id: toastId });
      /**
       * Set form type
       * TO shows the verify otp form
       */
      setFormType('verifyOtp');
    } catch (e) {
      toast.error('Something went wrong. Please try again.', { id: toastId });
      logger.error('Error in sending forgot password email', e, { context: 'ForgotPasswordForm' });
    }
  };

  /**
   * Handle email Otp Verify submission
   */
  const onEmailOtpVerify = async (values) => {
    const toastId = toast;
    try {
      const data = {
        email: email,
        otp: values.otp,
      };
      toast.loading('OTP verifying...', { id: toastId });

      const res = await VerifyEmailOTP(data);

      if (!res.success) toast.error('Something went wrong. Please try again.', { id: toastId });
      toast.success('Otp verified successfully.', { id: toastId });
      /**
       * Set form type
       * TO shows the reset password form
       */
      setFormType('resetPassword');
    } catch (e) {
      toast.error('Invalid OTP.', { id: toastId });
      setError('otp', { message: 'Invalid OTP.' });
      logger.error('Error in verifying email otp', e, { context: 'ForgotPasswordForm' });
    }
  };

  /**
   * Handle reset password
   */
  const onResetPassword = async (values) => {
    const toastId = toast;
    try {
      const data = {
        email: email,
        otp: otp,
        password: values.password,
      };

      toast.loading('Updating password...', { id: toastId });

      const res = await ResetPassword(data);

      if (!res.success) toast.error('Something went wrong. Please try again.', { id: toastId });
      toast.success('Password updated successfully.', { id: toastId });
      /**
       * on reset password success
       * redirect to login
       */
      router.replace('/login');
    } catch (e) {
      toast.error('Something went wrong. Please try again.', { id: toastId });
      logger.error('Error in resetting password', e, { context: 'ForgotPasswordForm' });
    }
  };

  return (
    <>
      <Card className={'w-full max-w-md'}>
        {formType === 'forgotPassword' && (
          <>
            <CardHeader className="text-center">
              <CardTitle className={'heading'}>Forgot Password</CardTitle>
              <CardDescription>
                Enter your registered email and we will send you a mail to reset your password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...sendEmailForm}>
                <form onSubmit={sendEmailForm.handleSubmit(onEmailSend)} className="space-y-6">
                  <FormField
                    control={sendEmailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="name@example.com"
                            type="email"
                            {...field}
                            disabled={isSendEmailSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSendEmailSubmitting}
                    isLoading={isSendEmailSubmitting}
                  >
                    Continue →
                  </Button>
                </form>
              </Form>
              <CardFooter className="mt-6 flex items-center justify-center text-sm">
                Don&apos;t have an account?
                <Link href="/sign-up" className="ml-1 underline underline-offset-4">
                  Sign up
                </Link>
              </CardFooter>
            </CardContent>
          </>
        )}

        {formType === 'verifyOtp' && (
          <>
            <CardHeader className="text-center">
              <CardTitle className={'heading'}>Verify Opt</CardTitle>
              <CardDescription>
                Enter the OTP sent to your email to reset your password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...verifyEmailOtpForm}>
                <form
                  onSubmit={verifyEmailOtpForm.handleSubmit(onEmailOtpVerify)}
                  className="space-y-6"
                >
                  <FormField
                    control={verifyEmailOtpForm.control}
                    name="otp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>One-Time Password</FormLabel>
                        <FormControl>
                          <InputOTP maxLength={6} {...field}>
                            <InputOTPGroup>
                              <InputOTPSlot index={0} className={'h-10 w-10'} />
                              <InputOTPSlot index={1} className={'h-10 w-10'} />
                            </InputOTPGroup>
                            <InputOTPSeparator />
                            <InputOTPGroup>
                              <InputOTPSlot index={2} className={'h-10 w-10'} />
                              <InputOTPSlot index={3} className={'h-10 w-10'} />
                            </InputOTPGroup>
                            <InputOTPSeparator />
                            <InputOTPGroup>
                              <InputOTPSlot index={4} className={'h-10 w-10'} />
                              <InputOTPSlot index={5} className={'h-10 w-10'} />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isVerifyEmailOtpSubmitting}
                    isLoading={isVerifyEmailOtpSubmitting}
                  >
                    Verify OTP
                  </Button>
                </form>
              </Form>
            </CardContent>
          </>
        )}

        {formType === 'resetPassword' && (
          <>
            <CardHeader className="text-center">
              <CardTitle className={'heading'}>Reset Password</CardTitle>
              <CardDescription>Enter your new password to reset your password.</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...resetPasswordForm}>
                <form
                  onSubmit={resetPasswordForm.handleSubmit(onResetPassword)}
                  className="space-y-6"
                >
                  <FormField
                    control={resetPasswordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="********"
                            type="password"
                            {...field}
                            disabled={isResetPasswordSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={resetPasswordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="********"
                            type="password"
                            {...field}
                            disabled={isResetPasswordSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isResetPasswordSubmitting}
                    isLoading={isResetPasswordSubmitting}
                  >
                    Submit
                  </Button>
                </form>
              </Form>
            </CardContent>
          </>
        )}
      </Card>
    </>
  );
};
